import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { useOFEFetch } from './useOFE';

export default function OFEProjectDashboard() {
  const { id: project } = useParams();
  const { user } = useAuth();
  const { data: summary } = useOFEFetch(`/ofe/production/status-summary/${project}`, user, [project]);
  const { data: financials } = useOFEFetch(`/ofe/financials/projects/${project}`, user, [project]);
  const { data: cx } = useOFEFetch(`/ofe/cx/lookahead/${project}`, user, [project]);
  const { data: delivery } = useOFEFetch(`/ofe/logistics/pending-deliveries/${project}`, user, [project]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-4">
        <Link to="/ofe" className="text-gray-400 hover:text-white text-sm">← OFE</Link>
        <h1 className="text-xl font-bold text-white">Project: <span className="text-blue-400 font-mono">{project}</span></h1>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Items', value: summary?.total ?? '—', color: 'text-white' },
          { label: 'In Production', value: summary?.by_status?.in_production ?? 0, color: 'text-yellow-400' },
          { label: 'Delivered', value: summary?.by_status?.delivered ?? 0, color: 'text-green-400' },
          { label: 'Overdue', value: delivery?.overdue_count ?? 0, color: 'text-red-400' },
        ].map(m => (
          <div key={m.label} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className={`text-3xl font-bold ${m.color}`}>{m.value}</div>
            <div className="text-gray-400 text-sm mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold">Financial Position</h3>
            <Link to={`/ofe/projects/${project}/financials`} className="text-blue-400 text-xs hover:text-blue-300">View all →</Link>
          </div>
          {financials ? (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-gray-400">PO Value</dt><dd className="text-white">${financials.total_po_value?.toLocaleString()}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Invoiced</dt><dd className="text-green-400">${financials.total_invoiced?.toLocaleString()}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Open Exposure</dt><dd className="text-yellow-400">${financials.open_exposure?.toLocaleString()}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Pending JEs</dt><dd className="text-purple-400">{financials.pending_je_count}</dd></div>
            </dl>
          ) : <div className="text-gray-500 text-sm">Loading...</div>}
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold">Cx Status</h3>
            <Link to={`/ofe/cx`} className="text-blue-400 text-xs hover:text-blue-300">View all →</Link>
          </div>
          {cx ? (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-gray-400">Upcoming Milestones</dt><dd className="text-white">{cx.upcoming_milestones?.length}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Open Issues</dt><dd className="text-yellow-400">{cx.open_issues}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Critical Issues</dt><dd className="text-red-400">{cx.critical_issues}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Docs Pending</dt><dd className="text-orange-400">{cx.closeout_docs_pending}</dd></div>
            </dl>
          ) : <div className="text-gray-500 text-sm">Loading...</div>}
        </div>
      </div>

      <div className="flex gap-3">
        <Link to={`/ofe/production/project/${project}`} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm">Production Planner</Link>
        <Link to={`/ofe/projects/${project}/schedule`} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm">Schedule Tracker</Link>
        <Link to={`/ofe/projects/${project}/financials`} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm">Financial Tracker</Link>
        <Link to={`/ofe/projects/${project}/docs`} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm">Documentation</Link>
      </div>
    </div>
  );
}
